/**
 * CSV Export Utility
 * Provides functions to export table data to CSV format
 */

import type { TableColumn, TableRow, CellData } from '../components/ResearchTable/types';
import type { Creator } from '../components/Creators/creatorTypes';

export interface ExportOptions {
  includeCreatorDetails?: boolean;
  includeMetadata?: boolean; // source, reasoning, confidence
  includeEmptyCells?: boolean;
  hiddenColumns?: Set<string>; // Column IDs to exclude from export
}

/**
 * Escape CSV field value to handle commas, quotes, and newlines
 */
const escapeCsvValue = (value: string): string => {
  if (value == null) return '';
  
  const stringValue = String(value);
  
  // If the value contains comma, quote, or newline, wrap in quotes and escape internal quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  
  return stringValue;
};

/**
 * Get primary platform handle for export
 */
const getPrimaryPlatformHandle = (creator: Creator): string => {
  // Return the handle from the first available platform (TikTok priority, then Instagram)
  if (creator.tiktok_data && creator.tiktok_data.length > 0) {
    return `@${creator.tiktok_data[0].handle}`;
  }
  
  if (creator.instagram_data && creator.instagram_data.length > 0) {
    return `@${creator.instagram_data[0].handle}`;
  }
  
  return '';
};

/**
 * Get creator email for export
 */
const getCreatorEmail = (creator: Creator): string => {
  const emails: string[] = [];
  
  if (creator.contacts && Array.isArray(creator.contacts)) {
    creator.contacts.forEach(contact => {
      if (contact.type === 'email' && contact.value) {
        emails.push(contact.value);
      }
    });
  }
  
  // Fallback to creator_shipping email
  if (emails.length === 0 && creator.creator_shipping?.email) {
    emails.push(creator.creator_shipping.email);
  }
  
  return emails.join(', ');
};

/**
 * Export research table data to CSV format
 */
export const exportTableToCSV = (
  columns: TableColumn[],
  rows: TableRow[],
  tableData: Record<string, Record<string, CellData>>,
  tableName: string = 'Research Table',
  options: ExportOptions = {}
): string => {
  const {
    includeCreatorDetails = true,
    includeMetadata = false,
    includeEmptyCells = true,
    hiddenColumns = new Set()
  } = options;
  
  // Build CSV headers
  const headers: string[] = [];
  
  // Creator info headers - simplified to just handle and email
  headers.push('Handle', 'Email');
  
  // Column headers - filter out unwanted and hidden columns
  columns.forEach(column => {
    // Skip ROI columns entirely
    if (column.name.toLowerCase().includes('roi')) {
      return;
    }
    
    // Skip hidden columns
    if (hiddenColumns.has(column.id)) {
      return;
    }
    
    // Always just export the column value, no metadata
    headers.push(column.name);
  });
  
  // Start building CSV content
  const csvLines: string[] = [];
  csvLines.push(headers.map(escapeCsvValue).join(','));
  
  // Process each row
  rows.forEach(row => {
    const csvRow: string[] = [];
    
    // Creator information - only handle and email
    const handle = getPrimaryPlatformHandle(row.creator);
    const email = getCreatorEmail(row.creator);
    
    csvRow.push(
      escapeCsvValue(handle),
      escapeCsvValue(email)
    );
    
    // Column data - filter out unwanted and hidden columns
    columns.forEach(column => {
      // Skip ROI columns entirely
      if (column.name.toLowerCase().includes('roi')) {
        return;
      }
      
      // Skip hidden columns
      if (hiddenColumns.has(column.id)) {
        return;
      }
      
      const cellData = tableData[row.id]?.[column.id];
      const value = cellData?.value || '';
      const hasData = value && !cellData?.error;
      
      // Skip empty cells if option is disabled
      if (!includeEmptyCells && !hasData) {
        csvRow.push(''); // Empty value
        return;
      }
      
      // Always just export the column value, no metadata
      csvRow.push(escapeCsvValue(value));
    });
    
    csvLines.push(csvRow.join(','));
  });
  
  return csvLines.join('\n');
};

/**
 * Copy CSV content to clipboard
 */
export const copyToClipboard = async (csvContent: string): Promise<boolean> => {
  try {
    // Convert CSV to tab-separated values for better spreadsheet compatibility
    const tsvContent = csvContent
      .split('\n')
      .map(line => {
        // Parse CSV line and convert to TSV
        const cells = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          const nextChar = line[i + 1];
          
          if (char === '"' && !inQuotes) {
            inQuotes = true;
          } else if (char === '"' && inQuotes && nextChar === '"') {
            current += '"';
            i++; // Skip next quote
          } else if (char === '"' && inQuotes) {
            inQuotes = false;
          } else if (char === ',' && !inQuotes) {
            cells.push(current);
            current = '';
          } else {
            current += char;
          }
        }
        cells.push(current); // Add last cell
        
        return cells.join('\t');
      })
      .join('\n');

    await navigator.clipboard.writeText(tsvContent);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    
    // Fallback for older browsers
    try {
      const textArea = document.createElement('textarea');
      textArea.value = csvContent;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    } catch (fallbackError) {
      console.error('Fallback copy failed:', fallbackError);
      return false;
    }
  }
};

/**
 * Download CSV file
 */
export const downloadCSV = (csvContent: string, filename: string = 'research-table.csv'): void => {
  // Create blob with UTF-8 BOM for proper Excel compatibility
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  
  // Create download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up
  URL.revokeObjectURL(url);
};

/**
 * Generate filename based on table name and current date
 */
export const generateExportFilename = (tableName: string): string => {
  const date = new Date();
  const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD format
  const timeStr = date.toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS format
  
  // Sanitize table name for filename
  const sanitizedTableName = tableName
    .replace(/[^a-zA-Z0-9\s-_]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .toLowerCase();
  
  return `${sanitizedTableName}-${dateStr}-${timeStr}.csv`;
};
